import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, media } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H4, Text } from 'app/common/typography'
import { Error, Input } from 'app/common/forms'
import { object, ref, string } from 'yup'
import { regex } from 'shared/utils'
import onClickOutside from 'react-onclickoutside'
import { ModalTitle } from 'app/common/modals'
import type { ChangeEmailForm as UpdateEmailFormType } from 'shared/types'
import type { RootState } from 'shared/store/reducers'
import {
    resetEmailUpdateSuccess,
    updateEmail
} from 'shared/store/kingdom/profile'
import { Recaptcha } from 'app/common/recaptcha'

const { grey, red, yellow } = colors

type Props = {
    handleClose: () => void
}

let recaptchaPromise: Promise<string>
let resolvePromise: any
let rejectPromise: any

const _UpdateEmailForm = (props: Props) => {
    const dispatch = useDispatch()
    const { email, updateEmailSuccess, updateEmailError } = useSelector(
        (state: RootState) => state.kingdomProfilePageState
    )
    const { handleClose } = props
    const [showCaptcha, setShowCaptcha] = useState(false)

    useEffect(() => {
        initRecaptchaPromise()
    }, [])
    useEffect(() => {
        if (updateEmailSuccess) {
            handleClose()
        }
    }, [updateEmailSuccess])

    const initRecaptchaPromise = () => {
        recaptchaPromise = new Promise((resolve, reject) => {
            resolvePromise = resolve
            rejectPromise = reject
        })
    }

    const verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            resolvePromise(recaptchaToken)
        } else {
            rejectPromise('Error getting recaptcha token')
        }
    }

    const initialValues: { newEmail: string, confirmedEmail: string } = {
        newEmail: '',
        confirmedEmail: ''
    }

    const formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            newEmail: string()
                .required(mandatory)
                .matches(regex.EMAIL, {
                    message: 'form.error.regex.email',
                    excludeEmptyString: true
                }),
            confirmedEmail: string()
                .required(mandatory)
                .oneOf([ref('newEmail'), null], 'form.email.update.match.error')
        })
    }

    return (
        <Content>
            <ModalTitle
                center
                handleClose={handleClose}
                titleNode={
                    <FormattedMessage id="kingdom.profile.form.email.title">
                        {(text: string) => <StyledH4>{text}</StyledH4>}
                    </FormattedMessage>
                }
            />
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={async (values, actions) => {
                    dispatch(resetEmailUpdateSuccess())
                    try {
                        setShowCaptcha(true)
                        const recaptchaToken = await recaptchaPromise
                        const postedData: UpdateEmailFormType = {
                            previous: email,
                            newEmail: values.newEmail,
                            confirmedEmail: values.confirmedEmail,
                            queen: recaptchaToken
                        }
                        await dispatch(updateEmail(postedData))
                    } finally {
                        actions.setSubmitting(false)
                        setShowCaptcha(false)
                        initRecaptchaPromise()
                    }
                }}
                validationSchema={formSubscription}>
                {({
                    handleChange,
                    handleSubmit,
                    handleBlur,
                    values,
                    errors,
                    touched,
                    isSubmitting
                }) => (
                    <Form>
                        <Input
                            name="newEmail"
                            labelKey="kingdom.profile.form.email.new.title"
                            placeholderKey="kingdom.profile.form.email.new.placeholder"
                            error={errors.newEmail && touched.newEmail}
                        />
                        {errors.newEmail && touched.newEmail && (
                            <Error errorKey={errors.newEmail} />
                        )}
                        <Input
                            name="confirmedEmail"
                            labelKey="kingdom.profile.form.email.new.confirmation.title"
                            placeholderKey="kingdom.profile.form.email.new.confirmation.placeholder"
                            isPastable={false}
                            error={
                                errors.confirmedEmail && touched.confirmedEmail
                            }
                        />
                        {errors.confirmedEmail && touched.confirmedEmail && (
                            <Error errorKey={errors.confirmedEmail} />
                        )}
                        <Mentions>
                            <FormattedMessage id="kingdom.profile.form.email.mentions">
                                {(text: string) => (
                                    <Text color={grey}>{text}</Text>
                                )}
                            </FormattedMessage>
                        </Mentions>
                        <ButtonContainer>
                            <StyledButtonMedium
                                onClick={handleClose}
                                background={yellow}
                                disabled={isSubmitting}>
                                <FormattedMessage id="form.button.cancel" />
                            </StyledButtonMedium>
                            <StyledButtonMedium
                                type="submit"
                                background={yellow}
                                disabled={isSubmitting}>
                                <FormattedMessage id="kingdom.profile.form.button.save" />
                            </StyledButtonMedium>
                        </ButtonContainer>
                        {updateEmailError && (
                            <Mentions>
                                <FormattedMessage id={updateEmailError}>
                                    {(text: string) => (
                                        <Text color={red}>{text}</Text>
                                    )}
                                </FormattedMessage>
                            </Mentions>
                        )}
                        {showCaptcha && (
                            <Recaptcha
                                action="UpdateEmail"
                                verifyCallback={verifyCallback}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        </Content>
    )
}

const Content = styled.div`
    padding: 0 20px;
    height: 100vh;
    ${media.medium`
        padding: 40px;
        height: auto;
    `};
`

const StyledH4 = styled(H4)`
    text-align: center;
`

const Mentions = styled.div`
    margin-top: 20px;
    ${props => (props.center ? 'text-align: center' : '')};
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 30px;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    width: calc(50% - 20px);
    border: none;
`

const UpdateEmailForm = onClickOutside(_UpdateEmailForm)

export { UpdateEmailForm }
